const DEFAULT_WAIT_BY_MODE = {
  lock: 800,
  debounce: 500,
  throttle: 1000,
};

const isPromiseLike = (value) =>
  value && typeof value.then === "function";

const normalizeMode = (mode) => {
  if (mode === "debounce" || mode === "throttle" || mode === "lock") {
    return mode;
  }

  return "lock";
};

const normalizeWait = (wait, mode) => {
  const value = Number(wait);

  if (Number.isFinite(value) && value >= 0) {
    return value;
  }

  return DEFAULT_WAIT_BY_MODE[mode];
};

const createLockGuard = (handler, wait) => {
  let locked = false;
  let releaseTimer = 0;
  let runSequence = 0;

  const releaseAfterWait = (startedAt, runSequenceAtStart) => {
    if (runSequenceAtStart !== runSequence) {
      return;
    }

    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(wait - elapsed, 0);

    releaseTimer = globalThis.setTimeout(() => {
      if (runSequenceAtStart !== runSequence) {
        return;
      }

      locked = false;
      releaseTimer = 0;
    }, remaining);
  };

  const guarded = function (...args) {
    if (locked) {
      return undefined;
    }

    locked = true;
    const startedAt = Date.now();
    const currentRunSequence = ++runSequence;

    try {
      const result = handler.apply(this, args);

      if (isPromiseLike(result)) {
        // 异步操作至少锁定 wait 毫秒，并且必须等 Promise 结束后才允许再次执行。
        return Promise.resolve(result).finally(() =>
          releaseAfterWait(startedAt, currentRunSequence),
        );
      }

      releaseAfterWait(startedAt, currentRunSequence);
      return result;
    } catch (error) {
      releaseAfterWait(startedAt, currentRunSequence);
      throw error;
    }
  };

  guarded.cancel = () => {
    // 使尚未结束的旧 Promise 无法在 cancel 后重新创建释放定时器。
    runSequence += 1;
    globalThis.clearTimeout(releaseTimer);
    releaseTimer = 0;
    locked = false;
  };

  return guarded;
};

const createDebounceGuard = (handler, wait) => {
  let timer = 0;

  const guarded = function (...args) {
    const context = this;

    globalThis.clearTimeout(timer);
    timer = globalThis.setTimeout(() => {
      timer = 0;
      handler.apply(context, args);
    }, wait);
  };

  guarded.cancel = () => {
    globalThis.clearTimeout(timer);
    timer = 0;
  };

  return guarded;
};

const createThrottleGuard = (handler, wait) => {
  let lastRunAt = 0;

  const guarded = function (...args) {
    const now = Date.now();

    if (now - lastRunAt < wait) {
      return undefined;
    }

    lastRunAt = now;
    return handler.apply(this, args);
  };

  guarded.cancel = () => {
    lastRunAt = 0;
  };

  return guarded;
};

export const createActionGuard = (handler, options = {}) => {
  if (typeof handler !== "function") {
    throw new TypeError("createActionGuard requires a function handler");
  }

  const mode = normalizeMode(options.mode);
  const wait = normalizeWait(options.wait, mode);

  if (mode === "debounce") {
    return createDebounceGuard(handler, wait);
  }

  if (mode === "throttle") {
    return createThrottleGuard(handler, wait);
  }

  return createLockGuard(handler, wait);
};

export default createActionGuard;
